import {
  useAddToOngoingCallOption,
  useChangePrivacyOption,
  useChangeToInPersonOption,
  useChangeToPhoneOption,
  useChangeToRingCentralOption,
  useEndMeetingOption,
  useGroupDividerOption,
} from './AdditionalOptions'
import { HeaderResultOption } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/ResultCard/Header/HeaderResultOptions/HeaderResultOptionsButton'
import React, { Fragment, useMemo } from 'react'
import { RoomSession } from '../../models/Room'
import { AdditionalOptionsButton } from './AdditionalOptionsButton'
import { useAppSelector } from '../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'

interface ChangeMeetingOptionsProps {
  session: RoomSession
  onSessionChange?: (session: RoomSession) => void
  phoneConfig?: MeetingProviderAccountIntegrationConfig | null
  onSessionSelectedForCall?: (details: { session: RoomSession; doCall: boolean }) => void
  skipEndMeeting?: boolean
  buttonLabel?: string
  hidePrivacyOption?: boolean
}

export const ChangeMeetingOptions: React.FC<ChangeMeetingOptionsProps> = ({
  session,
  onSessionChange,
  phoneConfig = null,
  onSessionSelectedForCall = undefined,
  skipEndMeeting = false,
  buttonLabel = undefined,
  hidePrivacyOption = false,
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const endMeetingOption = useEndMeetingOption(session)
  const addToOngoingCallOption = useAddToOngoingCallOption(session, phoneConfig ?? null, onSessionSelectedForCall)
  const platformGroupDividerOption = useGroupDividerOption('platform')
  const { resultOption: changeToInPersonOption, isLoading: changeToInPersonLoading } = useChangeToInPersonOption(session, onSessionChange)
  const { resultOption: changeToPhoneOption, isLoading: changeToPhoneLoading } = useChangeToPhoneOption(session, onSessionChange)
  const { resultOption: changeToRingCentralOption, isLoading: changeToRingCentralLoading } = useChangeToRingCentralOption(session, onSessionChange)
  const visibilityGroupDividerOption = useGroupDividerOption('visibility')
  const { resultOption: changePrivacyOption, isLoading: changePrivacyLoading } = useChangePrivacyOption(session, onSessionChange)
  const generalOptionExists = (!skipEndMeeting && !!endMeetingOption) || !!addToOngoingCallOption
  const platformOptionExists = !!changeToInPersonOption || !!changeToPhoneOption || !!changeToRingCentralOption
  const visibilityOptionExists = !hidePrivacyOption && !!changePrivacyOption

  const additionalOptions = useMemo(() => {
    const additionalOptions: HeaderResultOption[] = []
    if (!skipEndMeeting && !!endMeetingOption) {
      additionalOptions.push(endMeetingOption)
    }
    if (addToOngoingCallOption) {
      additionalOptions.push(addToOngoingCallOption)
    }
    if (generalOptionExists && platformOptionExists) {
      additionalOptions.push(platformGroupDividerOption)
    }
    if (changeToPhoneOption) {
      additionalOptions.push(changeToPhoneOption)
    }
    if (changeToRingCentralOption) {
      additionalOptions.push(changeToRingCentralOption)
    }
    if (changeToInPersonOption) {
      additionalOptions.push(changeToInPersonOption)
    }
    if (visibilityOptionExists && (generalOptionExists || platformOptionExists)) {
      additionalOptions.push(visibilityGroupDividerOption)
    }
    if (!hidePrivacyOption && changePrivacyOption) {
      additionalOptions.push(changePrivacyOption)
    }
    return additionalOptions
  }, [
    addToOngoingCallOption,
    changePrivacyOption,
    changeToInPersonOption,
    changeToPhoneOption,
    changeToRingCentralOption,
    endMeetingOption,
    generalOptionExists,
    hidePrivacyOption,
    platformGroupDividerOption,
    platformOptionExists,
    skipEndMeeting,
    visibilityGroupDividerOption,
    visibilityOptionExists,
  ])

  const extraChildren = (
    <>
      {additionalOptions.map(({ children }, idx) => (
        <Fragment key={idx}>{children}</Fragment>
      ))}
    </>
  )

  const RecurringEventDropdownHeader = () => {
    return (
      <div className='zocks-bg-color-gray-200 rounded p-2'>
        <div className='d-flex align-self-center gap-2 align-items-center'>
          <i className='bi bi-info-square zocks-text-gray-900 recurring-event-info-square' />
          <span className='paragraph-small zocks-text-gray-900'>Changes affect this occurrence only.</span>
        </div>
      </div>
    )
  }

  return session.roomCreatedBy?.id === currentUserId ? (
    <>
      <AdditionalOptionsButton
        disabled={additionalOptions.length === 0}
        resultOptions={additionalOptions}
        headerComponent={session.config?.isRecurring ? RecurringEventDropdownHeader : undefined}
        buttonLabel={buttonLabel}
        loading={changeToInPersonLoading || changeToPhoneLoading || changePrivacyLoading || changeToRingCentralLoading}
      />
      {extraChildren}
    </>
  ) : null
}
